import { useState } from "react";
import { Link } from "react-router-dom";
import { C, HEADSHOT, NEXA_LOGO, EHL_LOGO } from "../../data/content";
import { Countdown, DATE_LABEL } from "./Countdown";

const toForm = () => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });

const learn = [
  "How your VA entitlement actually works — and why it can mean $0 down for eligible buyers",
  "The soft-pull review process that shows where you stand without a hard credit inquiry",
  "The financing mistakes that cost VA buyers their deals — and how to avoid them on a resale home",
  "Exactly what to prepare now so you can move inside your 60–90 day buying window",
  "Your next step: how the free 1:1 personal review works after the webinar",
];

export const LearnSection = () => (
  <section className="py-20 px-4 lg:px-6" data-testid="learn-section-v2">
    <div className="max-w-[800px] mx-auto">
      <p className="text-[11px] font-bold tracking-[0.2em] text-[#444CE7] mb-3">WHAT YOU'LL LEARN</p>
      <h2 className="text-[clamp(26px,3.2vw,40px)] font-black text-[#1D2939] tracking-[-0.02em] mb-10">
        60 minutes. Zero fluff. Everything a VA buyer needs to move with confidence.
      </h2>
      <ul className="space-y-4">
        {learn.map((item, i) => (
          <li key={i} className="flex gap-4 items-start bg-white border border-[#EAECF0] rounded-xl p-5 shadow-sm" data-testid={`learn-bullet-${i + 1}`}>
            <span className="w-7 h-7 rounded-full bg-[#444CE7]/10 text-[#444CE7] font-black text-[13px] flex items-center justify-center shrink-0">✓</span>
            <p className="text-[15px] text-[#475467] leading-relaxed">{item}</p>
          </li>
        ))}
      </ul>
      <button onClick={toForm} className="mt-10 bg-[#444CE7] hover:bg-[#3538CD] text-white font-bold px-8 py-4 rounded-full transition-colors" data-testid="learn-cta">
        Save My Seat →
      </button>
    </div>
  </section>
);

const forYou = [
  "VA-eligible veterans or active-duty military planning to buy a home",
  "640+ credit score and roughly $90K+ household income",
  "Shopping resale homes around $250K and up, with ~$10K saved",
  "Planning to buy within the next 60–90 days",
  "Buying in Florida (Orlando, Tampa), Texas (Houston, Austin, San Antonio, DFW), or Pennsylvania",
];
const notForYou = [
  "Buyers focused on new-construction homes (this training covers resale purchases)",
  "Anyone who isn't VA-eligible (no VA entitlement to work with)",
  "Buyers more than a year away from purchasing",
  "Anyone expecting an exact qualification number on the webinar — exact figures come from your free 1:1 personal review afterward",
];

export const FitSection = () => (
  <section className="py-20 px-4 lg:px-6 bg-white border-y border-[#EAECF0]" data-testid="fit-section-v2">
    <div className="max-w-[1000px] mx-auto">
      <p className="text-[11px] font-bold tracking-[0.2em] text-[#444CE7] mb-3 text-center">IS THIS FOR YOU?</p>
      <h2 className="text-[clamp(26px,3.2vw,40px)] font-black text-[#1D2939] tracking-[-0.02em] mb-12 text-center">
        Built for serious VA buyers — not everyone.
      </h2>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-[#444CE7]/25 bg-[#444CE7]/[0.04] p-7" data-testid="for-you-card">
          <h3 className="font-black text-[#1D2939] text-lg mb-5">✅ This is for you if…</h3>
          <ul className="space-y-3">
            {forYou.map((t, i) => (
              <li key={i} className="flex gap-3 text-[14px] text-[#475467] leading-relaxed">
                <span className="text-[#444CE7] font-black shrink-0">✓</span>{t}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-[#EAECF0] bg-[#F9FAFB] p-7" data-testid="not-for-you-card">
          <h3 className="font-black text-[#1D2939] text-lg mb-5">🚫 This is NOT for you if…</h3>
          <ul className="space-y-3">
            {notForYou.map((t, i) => (
              <li key={i} className="flex gap-3 text-[14px] text-[#475467] leading-relaxed">
                <span className="text-[#98A2B3] font-black shrink-0">✗</span>{t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export const AboutSection = () => (
  <section className="py-20 px-4 lg:px-6" data-testid="about-section-v2">
    <div className="max-w-[1000px] mx-auto grid md:grid-cols-[300px_1fr] gap-10 items-start">
      <div className="relative">
        <img src={HEADSHOT} alt="Darren Gussick, NEXA Mortgage" className="rounded-2xl w-full object-cover aspect-[4/5] border border-[#EAECF0] shadow-[0_8px_40px_rgba(16,24,40,0.1)]" />
        <div className="absolute -bottom-4 left-4 right-4 bg-white rounded-xl border border-[#EAECF0] shadow-md px-4 py-3 text-center">
          <p className="text-[13px] font-black text-[#1D2939]">{C.hostName}</p>
          <p className="text-[11px] text-[#98A2B3]">NMLS #{C.hostNmls} · Licensed FL, TX, PA</p>
        </div>
      </div>
      <div>
        <p className="text-[11px] font-bold tracking-[0.2em] text-[#444CE7] mb-3">YOUR HOST</p>
        <h2 className="text-[clamp(26px,3.2vw,40px)] font-black text-[#1D2939] tracking-[-0.02em] mb-6">
          25 years in the mortgage business. One focus: getting veterans home.
        </h2>
        <div className="space-y-4 text-[15px] text-[#475467] leading-relaxed">
          <p>
            Darren Gussick has worked inside the mortgage industry since 2001. Today, as a loan officer with NEXA Mortgage, he focuses on one group of buyers: VA-eligible veterans and active-duty military in Florida, Texas, and Pennsylvania.
          </p>
          <p>
            After two and a half decades on the lender's side of the table, Darren has seen the same pattern over and over — veterans who earned a powerful home-buying benefit but were never shown how to actually use it. Slow lenders, generic online forms, and vague answers keep good buyers on the sidelines.
          </p>
          <p>
            The Veteran Fast-Track Review webinar is his answer: a straight-talk, plain-English session on how VA buying really works — followed by an optional free 1:1 personal review for those who want to see their own picture up close.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-8">
          {[["Company", C.hostCompany], ["NMLS #", C.hostNmls], ["Company NMLS #", "1660690"], ["Licensed In", "FL, TX, PA"]].map(([k, v]) => (
            <div key={k} className="bg-white border border-[#EAECF0] rounded-xl p-4">
              <p className="text-[10px] font-bold tracking-[0.15em] text-[#98A2B3] uppercase mb-1">{k}</p>
              <p className="text-[14px] font-bold text-[#1D2939]">{v}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const TestimonialPlaceholder = () => (
  <section className="pb-20 px-4 lg:px-6" data-testid="testimonial-placeholder-v2">
    <div className="max-w-[800px] mx-auto border-2 border-dashed border-[#EAECF0] rounded-2xl p-8 text-center">
      <p className="text-[12px] font-mono tracking-[0.15em] text-[#98A2B3]">
        [ TESTIMONIALS PLACEHOLDER — approved client reviews can be added here on your go-ahead ]
      </p>
    </div>
  </section>
);

const faqs = [
  { q: "Is the webinar really free?", a: "Yes — 100% free. It's an educational session, about 60 minutes, with nothing to buy during or after." },
  { q: "Is this a loan application?", a: "No. Attending is not a loan application and doesn't start one. It's education on how VA home buying works. Not a commitment to lend." },
  { q: "Will attending affect my credit?", a: "No. There is no credit pull of any kind to register or attend. The optional 1:1 review afterward uses a soft pull, which does not affect your score." },
  { q: "Will I get my exact qualification number on the webinar?", a: "No — and be cautious of anyone who promises that in a group setting. The webinar teaches you how qualification works. Your specific numbers are covered in the free, optional 1:1 personal review afterward." },
  { q: "Is it live or recorded?", a: "Live — with time for questions. Two sessions are available (Tue, Oct 27 and Thu, Oct 29 at 7:30 PM ET) so you can pick the night that works. VA situations are personal, and a recording can't answer yours." },
  { q: "What happens after I register?", a: "You'll get a confirmation by email and text with the session link and a reminder before your chosen session — Tuesday, October 27 or Thursday, October 29, 2026 at 7:30 PM Eastern." },
  { q: "Do I have to talk to a loan officer afterward?", a: "No. The 1:1 personal review is optional and free. No pressure, no obligation — you decide if and when." },
  { q: "What states does this cover?", a: "Florida, Texas, and Pennsylvania. Darren Gussick is NMLS #122481 with NEXA Mortgage LLC (NMLS #1660690), licensed in all three." },
];

export const FAQV2 = () => {
  const [open, setOpen] = useState(-1);
  return (
    <section className="py-20 px-4 lg:px-6 bg-white border-y border-[#EAECF0]" data-testid="faq-section-v2">
      <div className="max-w-[760px] mx-auto">
        <p className="text-[11px] font-bold tracking-[0.2em] text-[#444CE7] mb-3 text-center">FAQ</p>
        <h2 className="text-[clamp(26px,3.2vw,40px)] font-black text-[#1D2939] tracking-[-0.02em] mb-10 text-center">Common questions</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="bg-[#F9FAFB] border border-[#EAECF0] rounded-xl overflow-hidden" data-testid={`faq-v2-item-${i + 1}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-v2-toggle-${i + 1}`}
                className="w-full flex items-center justify-between gap-4 p-5 text-left">
                <span className="font-bold text-[15px] text-[#1D2939]">{f.q}</span>
                <span className={`text-[#444CE7] text-xl font-light transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              <div className={`transition-all duration-300 overflow-hidden ${open === i ? "max-h-48" : "max-h-0"}`}>
                <p className="px-5 pb-5 text-[14px] text-[#475467] leading-relaxed">{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FinalCTA = () => (
  <section className="py-24 px-4 lg:px-6 bg-[#101828]" data-testid="final-cta-v2">
    <div className="max-w-[760px] mx-auto text-center">
      <p className="text-[11px] font-bold tracking-[0.2em] text-[#8098F9] mb-4">ONE LIVE SESSION — DON'T MISS IT</p>
      <h2 className="text-[clamp(28px,3.6vw,46px)] font-black text-white tracking-[-0.02em] mb-4">
        Your VA benefit is waiting.<br /><span className="text-[#8098F9]">Pick your night: Oct 27 or Oct 29.</span>
      </h2>
      <p className="text-[15px] text-white/60 mb-8" data-testid="final-date-v2">📅 {DATE_LABEL} · ~60 minutes · Free</p>
      <div className="flex justify-center mb-10"><Countdown /></div>
      <button onClick={toForm} data-testid="final-cta-button"
        className="bg-[#444CE7] hover:bg-[#3538CD] text-white font-bold text-[16px] px-10 py-4 rounded-full transition-all hover:shadow-[0_8px_30px_rgba(68,76,231,0.5)]">
        Save My Seat →
      </button>
      <p className="text-[12px] text-white/40 mt-4">Free · No obligation · No hard credit pull to attend</p>
    </div>
  </section>
);

export const FooterV2 = () => (
  <footer className="py-14 px-4 lg:px-6 bg-white border-t border-[#EAECF0]" data-testid="footer-v2">
    <div className="max-w-[1000px] mx-auto">
      <div className="flex flex-col md:flex-row justify-between gap-8 mb-10">
        <div>
          <img src={NEXA_LOGO} alt="NEXA Mortgage" className="h-10 w-auto mb-4" />
          <p className="text-[13px] text-[#475467] leading-relaxed">
            {C.hostName} · NMLS #{C.hostNmls}<br />
            NEXA Mortgage LLC · Company NMLS #1660690<br />
            Licensed in Florida, Texas & Pennsylvania
          </p>
        </div>
        <div className="text-[13px] text-[#475467] leading-relaxed">
          <p className="font-bold text-[#1D2939] mb-2">Contact</p>
          <p><a href="tel:4075505795" className="hover:text-[#444CE7]">407-550-5795</a></p>
          <p><a href="mailto:dgussick@nexalending.com" className="hover:text-[#444CE7]">dgussick@nexalending.com</a></p>
        </div>
        <div className="text-[13px] text-[#475467] leading-relaxed">
          <p className="font-bold text-[#1D2939] mb-2">Legal</p>
          <p><Link to="/privacy" className="hover:text-[#444CE7]" data-testid="footer-v2-privacy">Privacy Policy</Link></p>
          <p><Link to="/terms" className="hover:text-[#444CE7]" data-testid="footer-v2-terms">Terms & Conditions</Link></p>
        </div>
      </div>
      <div className="border-t border-[#EAECF0] pt-8 flex flex-col md:flex-row gap-5 items-start">
        <img src={EHL_LOGO} alt="Equal Housing Opportunity" className="w-10 h-auto invert opacity-80 shrink-0" />
        <p className="text-[11.5px] text-[#98A2B3] leading-relaxed" data-testid="footer-v2-compliance">
          Equal Housing Opportunity. This is an educational webinar. Attendance does not constitute a loan application and is <strong>not a commitment to lend</strong>. No credit pull required to attend. Results, approval, rate, and closing timelines are not guaranteed. Darren Gussick NMLS #122481, NEXA Mortgage LLC NMLS #1660690.
        </p>
      </div>
      <p className="text-[11px] font-mono text-[#98A2B3]/70 mt-6">© 2026 Darren Gussick / NEXA Mortgage</p>
    </div>
  </footer>
);
